import { TABS_CLASS_NAME } from "Common/componentUtils/constants";
import classNames from "classnames";
import debounce from "lodash/debounce";
import filter from "lodash/filter";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import Tab from "../tab";
import TabPanel from "../tabPanel";
import Styled, { SIZES } from "./tabs.styled";
const is = (item, type) => {
    if (!item)
        return false;
    return get(item, "type") === type || get(item, "type.target") === type;
};
const rightPanelStyle = {
    top: 0,
    right: 0
};
const containerGapStyle = {
    gap: SIZES.GAP
};
function Tabs({ containerClassName, selectedTabIndex, showSlider, className, tabsWrapperClassName, children, useGap, onTabChange, rightPanelWidth, flex }) {
    const tabsRefs = useRef([]);
    const slider = useRef(null);
    const flatChildren = useMemo(() => (children ? React.Children.toArray(children) : []), [children]);
    const updateSlider = useCallback(() => {
        var _a;
        if (slider.current && ((_a = tabsRefs.current) === null || _a === void 0 ? void 0 : _a.length)) {
            if (tabsRefs.current[selectedTabIndex]) {
                const activeTabRef = tabsRefs.current[selectedTabIndex];
                const clientRect = activeTabRef.getBoundingClientRect();
                slider.current.style.width = `${clientRect.width}px`;
                slider.current.style.left = `${activeTabRef.offsetLeft}px`;
            }
            else {
                slider.current.style.width = "0px";
            }
        }
    }, [selectedTabIndex, flatChildren]);
    useEffect(() => {
        updateSlider();
        const resizeCallback = debounce(updateSlider, 100);
        window.addEventListener("resize", resizeCallback);
        return () => {
            window.removeEventListener("resize", resizeCallback);
        };
    }, [showSlider, selectedTabIndex, flatChildren]);
    const renderTabs = (tabIndex) => {
        const tabsElements = filter(flatChildren, e => is(e, Tab));
        tabsRefs.current = [];
        return map(tabsElements, (item, index) => React.isValidElement(item) &&
            React.cloneElement(item, {
                active: index === tabIndex,
                className: classNames("nowrap text-overflow-ellipsis", item.props.className),
                key: `tab-${index}`,
                index,
                tabRef: (r) => {
                    tabsRefs.current[index] = r;
                },
                onClick: onTabChange
            }));
    };
    const tabPanel = useMemo(() => {
        const panelsElements = filter(flatChildren, e => is(e, TabPanel));
        return panelsElements[selectedTabIndex];
    }, [selectedTabIndex, flatChildren]);
    const rightPanel = useMemo(() => {
        var _a;
        const tabElement = filter(flatChildren, e => is(e, Tab))[selectedTabIndex];
        const tabRightPanel = React.isValidElement(tabElement)
            ? (_a = tabElement.props) === null || _a === void 0 ? void 0 : _a.rightPanel
            : null;
        return tabRightPanel ? (React.createElement("div", { className: flex ? undefined : "position-absolute", style: rightPanelStyle }, tabRightPanel)) : null;
    }, [selectedTabIndex, flatChildren]);
    const panelsElements = filter(flatChildren, e => is(e, TabPanel));
    return (React.createElement("div", { className: classNames(className, "tabs", !isEmpty(panelsElements) ? "grid-rows-auto-1fr h-100-max" : "d-grid"), style: useGap ? containerGapStyle : undefined },
        React.createElement("div", { className: classNames("tabs-container position-relative", containerClassName, flex && "flex-space-between flex-single-row align-items-f-end ") },
            React.createElement(Styled.ReactTabs, { rightPanelWidth: rightPanelWidth, className: classNames(TABS_CLASS_NAME, tabsWrapperClassName, "border-bottom", flex && "flex-fill-block") },
                renderTabs(selectedTabIndex),
                showSlider && React.createElement(Styled.Slider, { ref: slider })),
            rightPanel),
        tabPanel));
}
export default memo(Tabs);
Tabs.defaultProps = {
    selectedTabIndex: 0,
    showSlider: true,
    useGap: true,
    rightPanelWidth: 0
};
